import { types, flow } from 'mobx-state-tree'
import { apiV1 } from '../../utils'
import { UserModel } from 'stores/models/user'
import { toast } from 'react-toastify'
import { fromPromise } from 'mobx-utils'

export const UsersStore = types
  .model('UsersStore', {
    _data: types.optional(types.array(UserModel), []),
    _isPending: types.optional(types.boolean, false),
  })
  .views(self => ({
    get usersList() {
      return self._data
    },
    get isPending() {
      return self._isPending
    },
  }))
  .actions(self => {
    const API_URL = '/users'

    const setUsersList = usersList => {
      self._data.replace(usersList)
    }

    const getUsersList = flow(function* (params) {
      self._isPending = true
      try {
        const response = yield fromPromise(apiV1.get(API_URL, { params }))
        setUsersList(response.data)
      } catch (error) {
        console.error('Failed to fetch users list:', error)
      } finally {
        self._isPending = false
      }
    })

    const getUsersPageable = flow(function* (body) {
      self._isPending = true
      try {
        const response = yield fromPromise(apiV1.post(`${API_URL}/pageable`, body))
        setUsersList(response.data.items || response.data)
      } catch (error) {
        console.error('Failed to fetch pageable users:', error)
      } finally {
        self._isPending = false
      }
    })

    const addUser = flow(function* (userData) {
      self._isPending = true

      const payload = {
        ...userData,
        approvalLevel: Number(userData.approvalLevel),
        roleId: Number(userData.roleId),
        branchId: Number(userData.branchId),
        officeId: Number(userData.officeId),
        bankId: Number(userData.bankId),
        userName: userData.userName.length ? userData.userName : userData.phoneNumber,
      }

      try {
        const response = yield fromPromise(apiV1.post('Registration/new', payload))
        return response.data
      } catch (error) {
        const message = error?.response?.data?.errors?.Alert?.[0] || error?.response?.data?.message || 'Failed to add user'

        return { error: message }
      } finally {
        self._isPending = false
      }
    })

    const updateUser = flow(function* (userData) {
      self._isPending = true
      try {
        yield fromPromise(apiV1.put(`${API_URL}`, userData))

        const payload = {
          ...userData,
          roleId: userData.roleId ? Number(userData.roleId) : null,
          branchId: userData.branchId ? Number(userData.branchId) : null,
          officeId: userData.officeId ? Number(userData.officeId) : null,
          bankId: userData.bankId ? Number(userData.bankId) : null,
          approvalLevel: userData.approvalLevel ? Number(userData.approvalLevel) : null,
        }

        const index = self._data.findIndex(u => u.id === userData.id)
        if (index !== -1) {
          self._data[index] = {
            ...self._data[index],
            ...payload,
          }
        }
      } catch (error) {
        const message = error?.response?.data?.errors?.Alert?.[0] || error?.response?.data?.message || 'Ошибка при обновлении пользователя'

        return { error: message }
      } finally {
        self._isPending = false
      }
    })

    const addGroupUsers = flow(function* (bankId, officeId, file) {
      self._isPending = true
      try {
        const formData = new FormData()
        formData.append('file', file)

        const response = yield fromPromise(
          apiV1.post(`Registration/${bankId}/${officeId}/Import`, formData, {
            headers: { 'Content-Type': 'multipart/form-data', 'x-version': '1.0' },
          }),
        )

        return response.data
      } catch (error) {
        const message = error?.response?.data?.errors?.Alert?.[0] || error?.response?.data?.message || 'Ошибка при импорте пользователей'

        toast.warn(message)
      } finally {
        self._isPending = false
      }
    })

    const deleteUser = flow(function* (userId) {
      self._isPending = true
      try {
        yield fromPromise(apiV1.delete(`${API_URL}/${userId}`))

        const index = self._data.findIndex(u => u.id === userId)
        if (index !== -1) {
          self._data.splice(index, 1)
        }

        return true
      } catch (error) {
        const message = error?.response?.data?.errors?.Alert?.[0] || error?.response?.data?.message || 'Failed to delete the user'

        return { error: message }
      } finally {
        self._isPending = false
      }
    })

    const resetPassword = flow(function* (userId) {
      self._isPending = true
      try {
        const response = yield fromPromise(apiV1.patch(`${API_URL}/${userId}/resetpassword`))
        const data = response.data

        const index = self._data.findIndex(u => u.id === userId)
        if (index !== -1) {
          self._data[index] = {
            ...self._data[index],
            isFirstTimePasswordWord: data.password,
          }
        }

        return data
      } catch (error) {
        console.error('Failed to reset password:', error)
      } finally {
        self._isPending = false
      }
    })

    const validateGroupUsers = flow(function* (officeId, file, reportToUserId) {
      self._isPending = true
      try {
        const formData = new FormData()
        formData.append('file', file)

        const response = yield fromPromise(
          apiV1.post(`Registration/${officeId}/ValidateUsers`, formData, {
            params: { reportToUserId },
            headers: { 'Content-Type': 'multipart/form-data', 'x-version': '1.0' },
          }),
        )

        return response.data
      } catch (error) {
        const message = error?.response?.data?.errors?.Alert?.[0] || error?.response?.data?.message || 'Ошибка при валидации пользователей'

        return { error: message }
      } finally {
        self._isPending = false
      }
    })

    const importUsers = flow(function* (officeId, users, reportToUserId) {
      self._isPending = true
      try {
        const response = yield fromPromise(
          apiV1.post(`Registration/${officeId}/ImportUsers`, users, {
            params: { reportToUserId },
          }),
        )

        return response.data
      } catch (error) {
        const message = error?.response?.data?.errors?.Alert?.[0] || error?.response?.data?.message || 'Ошибка при импорте пользователей'

        return { error: message }
      } finally {
        self._isPending = false
      }
    })

    const checkUserInformation = flow(function* (users, officeId, reportToUserId) {
      self._isPending = true
      try {
        const response = yield fromPromise(
          apiV1.post(`Registration/${officeId}/checkUsersInformation?reportToUserId=${reportToUserId}`, users, { headers: { 'x-version': '1.0' } }),
        )

        return response.data
      } catch (error) {
        const message = error?.response?.data?.errors?.Alert?.[0] || error?.response?.data?.message || 'Ошибка при проверке данных пользователя'

        return { error: message }
      } finally {
        self._isPending = false
      }
    })

    const downloadExelTemplate = flow(function* () {
      self._isPending = true
      try {
        const response = yield fromPromise(apiV1.get(`Registration/templateExcelFile`))
        const a = document.createElement('a')
        const url = response.data
        a.href = url
        a.download = 'UserImportTemplate.xlsx'
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
        self._isPending = false
      } catch (error) {
        const message = error?.response?.data?.errors?.Alert?.[0] || error?.response?.data?.message || 'Ошибка при импорте пользователей'
      }
    })

    return {
      setUsersList,
      getUsersList,
      getUsersPageable,
      addUser,
      addGroupUsers,
      updateUser,
      deleteUser,
      resetPassword,
      validateGroupUsers,
      importUsers,
      checkUserInformation,
      downloadExelTemplate,
    }
  })

export default UsersStore
